/**
 * Is this court's information right? (Dani, 26 Oct 2026: wrong types, missing addresses and phones,
 * letters to wrong emails, bonds with wrong details.)
 *
 * Lists what still needs fixing, says whether a person has checked the court, and lets them mark it
 * checked. A court whose email looks wrong has its letters held at LOR@ until it is checked; the
 * panel says so, because otherwise "the LOR never went" looks like a fault.
 */
import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ManagerApi from '../../api/ManagerApi';
import { FullCourtDto } from '../../interfaces/CourtInterfaces';

const CourtCheckPanel: React.FC<{
  court: FullCourtDto;
  onChanged: (court: FullCourtDto) => void;
}> = ({ court, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems = court.Court_Problems ?? [];
  const held = problems.some((p) => p.blocksLor);
  const verifiedAt = court.Court_VerifiedAt ? new Date(court.Court_VerifiedAt) : null;

  const setVerified = async (verified: boolean) => {
    setSaving(true);
    setError(null);
    try {
      onChanged(await ManagerApi.verifyCourt(court.Court_guid, verified));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the check');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {problems.length > 0 && (
        <Alert severity={held ? 'error' : 'warning'} sx={{ mb: 1 }}>
          <AlertTitle>
            {held
              ? 'Letters to this court are held at LOR@ until someone checks its email'
              : `${problems.length} thing${problems.length === 1 ? '' : 's'} to check on this court`}
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {problems.map((p) => (
              <li key={p.code}>
                <Typography variant="body2">{p.message}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {verifiedAt ? (
          <>
            <VerifiedIcon color="success" fontSize="small" />
            <Typography variant="body2">
              Details checked{court.Court_VerifiedBy ? ` by ${court.Court_VerifiedBy}` : ''} on{' '}
              {verifiedAt.toLocaleDateString('en-US', { dateStyle: 'medium' })}.
            </Typography>
            <Button size="small" onClick={() => setVerified(false)} disabled={saving}>
              Remove check
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Nobody has checked this court's details yet.
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<VerifiedIcon />}
              onClick={() => setVerified(true)}
              disabled={saving}
            >
              Details are correct
            </Button>
          </>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Changing the name, email, address, phone, county, precinct or type takes the check off, so the
        new details are looked at too.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CourtCheckPanel;
